import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { RevealLines } from './Reveal';
import { IMG } from '../data/site';
import { ArrowDown } from 'lucide-react';

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="top" ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMG.hero}
          alt="Jordan — bartender Ô Fil des Verres en action derrière son bar mobile"
          className="w-full h-full object-cover object-[60%_22%]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/50 via-ink/40 to-ink/75" />
        <div className="absolute inset-0 bg-ink/25" />
      </motion.div>

      {/* Overline top-left */}
      <motion.div
        style={{ opacity }}
        className="absolute top-24 md:top-28 left-6 md:left-10 z-10 flex items-center gap-4"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.6 }}
      >
        <span className="w-10 h-px bg-gold" />
        <span className="overline text-white/90">Depuis 15 ans · Sud de la France</span>
      </motion.div>

      {/* Right side vertical marker */}
      <motion.div
        style={{ opacity }}
        className="hidden md:flex absolute top-1/2 -translate-y-1/2 right-8 flex-col items-center gap-4 z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 2 }}
      >
        <span className="overline text-white/60 [writing-mode:vertical-rl] rotate-180 tracking-[0.4em]">
          Montpellier · Occitanie · Sud de la France
        </span>
        <span className="w-px h-24 bg-gradient-to-b from-gold to-transparent" />
      </motion.div>

      {/* Main content */}
      <motion.div style={{ opacity }} className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-28 px-6 md:px-10 max-w-[1600px] mx-auto">
        <div className="mb-6 md:mb-10 flex items-center gap-6">
          <span className="w-16 h-px bg-gold" />
          <span className="overline text-gold">Bar à cocktails événementiel</span>
        </div>

        <h1 className="font-serif text-white leading-[0.9] tracking-tight" style={{ fontSize: 'clamp(3rem, 9vw, 9rem)' }}>
          <RevealLines
            mode="onLoad"
            lines={['Des cocktails', <span key="a"><em className="italic font-light text-gold/95">qui marquent</em></span>, 'vos événements.']}
            delay={0.6}
            stagger={0.12}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.8 }}
          className="mt-8 max-w-xl text-white/70 text-base md:text-lg font-light"
        >
          Mariages · Anniversaires · Entreprises · Soirées privées — Un bar mobile élégant, un bartender expérimenté, une nuit inoubliable.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2.1 }}
          className="mt-10 md:mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo('contact')}
            className="group relative inline-flex items-center gap-3 bg-gold text-ink px-8 py-4 text-[12px] tracking-[0.2em] uppercase font-medium hover:bg-white transition-colors duration-500"
            data-testid="hero-devis-btn"
          >
            Demander un devis
            <ArrowDown size={16} className="rotate-[-90deg] group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={() => scrollTo('cocktails')}
            className="text-white/80 hover:text-white text-[12px] tracking-[0.2em] uppercase link-underline"
            data-testid="hero-carte-btn"
          >
            Voir la carte
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        style={{ opacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="overline text-white/50">scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="w-px h-8 bg-white/40"
        />
      </motion.div>
    </section>
  );
}
